/**
 * JKL Custom Dropdown Component
 * Based on Figma Design System
 * 
 * Handles custom dropdown behavior including:
 * - Open/close toggle
 * - Option selection
 * - Click outside to close
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Accessibility (ARIA attributes)
 */

class JKLDropdown {
  constructor(element) {
    this.dropdown = element;
    this.trigger = element.querySelector('.jkl-dropdown__trigger');
    this.options = element.querySelector('.jkl-dropdown__options');
    this.optionElements = element.querySelectorAll('.jkl-dropdown__option');
    this.hiddenSelect = element.querySelector('.jkl-dropdown__select');
    this.triggerText = this.trigger.querySelector('.jkl-dropdown__trigger-text');
    
    console.log('JKL Dropdown: Constructor called, found', this.optionElements.length, 'options');
    
    this.isOpen = false;
    this.selectedValue = this.hiddenSelect.value;
    this.focusedIndex = -1;
    
    this.init();
  }
  
  init() {
    // Bind event listeners
    this.trigger.addEventListener('click', this.handleToggle.bind(this));
    this.trigger.addEventListener('keydown', this.handleTriggerKeydown.bind(this));
    
    this.optionElements.forEach((option, index) => {
      option.addEventListener('click', () => this.handleSelect(option));
      option.addEventListener('keydown', (e) => this.handleOptionKeydown(e, index));
    });
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside.bind(this));
    
    // Close dropdown on escape key
    document.addEventListener('keydown', this.handleEscape.bind(this));
  }
  
  handleToggle(e) {
    e.stopPropagation();
    
    if (this.trigger.disabled) return;
    
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
  
  handleTriggerKeydown(e) {
    if (this.trigger.disabled) return;
    
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        if (!this.isOpen) {
          this.open();
        }
        if (this.optionElements.length > 0) {
          this.focusedIndex = 0;
          this.optionElements[0].focus();
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          e.preventDefault();
          this.close();
        }
        break;
    }
  }
  
  handleOptionKeydown(e, index) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.handleSelect(this.optionElements[index]);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.focusNext(index);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.focusPrevious(index);
        break;
      case 'Escape':
        e.preventDefault();
        this.close();
        this.trigger.focus();
        break;
      case 'Tab':
        // Allow default tab behavior but close dropdown
        this.close();
        break;
    }
  }
  
  handleSelect(optionElement) {
    const value = optionElement.dataset.value;
    const label = optionElement.textContent.trim();
    
    // Update hidden select
    this.hiddenSelect.value = value;
    
    // Update trigger text
    this.triggerText.textContent = label;
    this.trigger.classList.add('jkl-dropdown__trigger--selected');
    
    // Update selected state on options
    this.optionElements.forEach(opt => {
      opt.classList.remove('jkl-dropdown__option--selected');
      opt.setAttribute('aria-selected', 'false');
    });
    
    optionElement.classList.add('jkl-dropdown__option--selected');
    optionElement.setAttribute('aria-selected', 'true');
    
    // Store selected value
    this.selectedValue = value;
    
    // Trigger change event on hidden select
    const changeEvent = new Event('change', { bubbles: true });
    this.hiddenSelect.dispatchEvent(changeEvent);
    
    // Close dropdown
    this.close();
    this.trigger.focus();
  }
  
  open() {
    console.log('JKL Dropdown: Opening dropdown');
    this.dropdown.classList.add('jkl-dropdown--open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    
    // Find and set focus index for selected option
    this.optionElements.forEach((opt, index) => {
      if (opt.classList.contains('jkl-dropdown__option--selected')) {
        this.focusedIndex = index;
      }
    });
  }
  
  close() {
    this.dropdown.classList.remove('jkl-dropdown--open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    this.focusedIndex = -1;
  }
  
  focusNext(currentIndex) {
    const nextIndex = (currentIndex + 1) % this.optionElements.length;
    this.focusedIndex = nextIndex;
    this.optionElements[nextIndex].focus();
  }
  
  focusPrevious(currentIndex) {
    const prevIndex = currentIndex === 0 
      ? this.optionElements.length - 1 
      : currentIndex - 1;
    this.focusedIndex = prevIndex;
    this.optionElements[prevIndex].focus();
  }
  
  handleClickOutside(e) {
    if (!this.dropdown.contains(e.target) && this.isOpen) {
      this.close();
    }
  }
  
  handleEscape(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
      this.trigger.focus();
    }
  }
  
  destroy() {
    // Clean up event listeners
    this.trigger.removeEventListener('click', this.handleToggle);
    this.trigger.removeEventListener('keydown', this.handleTriggerKeydown);
    
    this.optionElements.forEach((option) => {
      option.removeEventListener('click', () => this.handleSelect(option));
      option.removeEventListener('keydown', this.handleOptionKeydown);
    });
    
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleEscape);
  }
}

// Initialize all dropdowns on page load
const initDropdowns = () => {
  const dropdowns = document.querySelectorAll('[data-dropdown]');
  
  console.log('JKL Dropdown: Found', dropdowns.length, 'dropdowns to initialize');
  
  dropdowns.forEach(dropdown => {
    // Check if already initialized
    if (!dropdown.dataset.dropdownInitialized) {
      console.log('JKL Dropdown: Initializing dropdown', dropdown);
      new JKLDropdown(dropdown);
      dropdown.dataset.dropdownInitialized = 'true';
    }
  });
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDropdowns);
} else {
  initDropdowns();
}

// Re-initialize on Shopify section load events (for theme editor)
if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', initDropdowns);
  document.addEventListener('shopify:section:reorder', initDropdowns);
}

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = JKLDropdown;
}

